/**
 * Miller Harris — GWP Cart Manager
 *
 * Reads GWP rules from the Vercel API (with Liquid snippet as offline
 * fallback) and automatically adds or removes gift products from the
 * cart based on eligibility.
 *
 * Handles:
 *   - minimum_spend: gift added when cart subtotal meets threshold
 *   - specific_product: gift added when a qualifying product is in the cart
 *   - discount_code: gift added when customer enters a promo code in the cart
 *   - none: gift is always added
 *
 * The Product Discount Function zeros the gift price at checkout.
 *
 * IMPORTANT: rules are sourced from the live Vercel API first. The Liquid
 * shop metafield is only used as a fallback if the API is unreachable —
 * never as a primary source. This is because the metafield is a cache that
 * can go stale if a discount is deleted/disabled directly in the Shopify
 * Discounts admin without anyone re-syncing via the config page. Trusting
 * stale rules causes the JS to add a gift that no longer has a backing
 * discount, which charges the customer at full price.
 */

(function () {
  'use strict';

  const RULES_ELEMENT_ID = 'mh-gwp-rules';
  const RULES_API_URL = 'https://mh-functions.vercel.app/api/rules?shop=' + (window.Shopify?.shop || window.location.hostname);
  const GWP_CART_PROPERTY = '_gwp_rule';
  const APPLIED_CODES_KEY = '_mh_gwp_codes';
  const DISMISSED_GIFTS_KEY = '_mh_gwp_dismissed';

  let rules = [];
  let rulesReady = false;       // true once API has returned (or failed and Liquid was used)
  let processing = false;
  let appliedCodes = [];
  let dismissedGifts = [];

  function init() {
    try {
      appliedCodes = JSON.parse(sessionStorage.getItem(APPLIED_CODES_KEY) || '[]');
    } catch { appliedCodes = []; }
    try {
      dismissedGifts = JSON.parse(sessionStorage.getItem(DISMISSED_GIFTS_KEY) || '[]');
    } catch { dismissedGifts = []; }

    var el = document.getElementById(RULES_ELEMENT_ID);

    // Fetch live rules from the Vercel API. We DO NOT bootstrap from Liquid
    // first — Liquid is a cache that may contain a stale rule whose discount
    // has been deleted/disabled, and acting on it would auto-add a gift the
    // customer then gets charged for at checkout.
    fetch(RULES_API_URL)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (freshRules) {
        if (!Array.isArray(freshRules)) freshRules = [];
        rules = freshRules;
        if (el) { el.textContent = JSON.stringify(freshRules); }
        rulesReady = true;
        startWithRules();
      })
      .catch(function () {
        // API failed — fall back to Liquid so eligibility checks still work.
        // Orphan cleanup in checkCart will still scrub gifts whose rule isn't
        // in this fallback set.
        if (el) {
          try { rules = JSON.parse(el.textContent) || []; } catch { rules = []; }
        }
        rulesReady = true;
        startWithRules();
      });
  }

  function startWithRules() {
    var hasCodeRules = rules.some(function (r) { return r.discountCode; });
    if (hasCodeRules) {
      injectPromoCodeInput();
      preInjectPromoInput();
    }
    checkCart();
    observeCartChanges();
  }

  // Pre-inject promo code input into cart drawer so it's ready before drawer opens
  function preInjectPromoInput() {
    var hasCodeRules = rules.some(function (r) { return r.discountCode; });
    if (!hasCodeRules) return;
    injectPromoCodeInput();
    getCart().then(function (cart) { updatePromoInputVisibility(cart); });
    if (!document.getElementById('mh-gwp-promo-input')) {
      var injectObserver = new MutationObserver(function () {
        if (document.querySelector('[data-cart-drawer]') && !document.getElementById('mh-gwp-promo-input')) {
          injectPromoCodeInput();
          getCart().then(function (cart) { updatePromoInputVisibility(cart); });
          injectObserver.disconnect();
        }
      });
      injectObserver.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { injectObserver.disconnect(); }, 10000);
    }
  }

  // ── Cart API helpers ──────────────────────────────────────────────────

  async function getCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    return res.json();
  }

  async function addToCart(variantId, properties) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: properties || {}
        }]
      })
    });
    return res.json();
  }

  async function removeFromCart(lineKey) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });
    return res.json();
  }

  // ── Promo code input ─────────────────────────────────────────────────

  function injectPromoCodeInput() {
    if (document.getElementById('mh-gwp-promo-input')) return;

    var cartFooter = document.querySelector('[data-cart-drawer] a[href="/cart"]')?.closest('div') ||
      document.querySelector('cart-drawer a[href="/cart"]')?.closest('div.px-2, div.flex-col') ||
      document.querySelector('form[action="/cart"]') ||
      document.querySelector('[data-cart-form]');

    if (!cartFooter) return;

    var wrapper = document.createElement('div');
    wrapper.id = 'mh-gwp-promo-wrapper';
    wrapper.style.cssText = 'padding: 12px 0; border-top: 1px solid #e1e3e5; margin-bottom: 12px; display: none;';

    wrapper.innerHTML =
      '<label for="mh-gwp-promo-input" style="display:block; font-size:12px; font-weight:500; margin-bottom:6px; text-transform:uppercase; letter-spacing:0.5px;">Have a gift code?</label>' +
      '<div style="display:flex; gap:8px;">' +
        '<input type="text" id="mh-gwp-promo-input" placeholder="Enter code" style="flex:1; padding:8px 12px; border:1px solid #c9cccf; border-radius:4px; font-size:14px; outline:none;">' +
        '<button type="button" id="mh-gwp-promo-btn" class="btn btn-brand-charcoal" style="padding:8px 16px; font-size:12px; white-space:nowrap;">Apply</button>' +
      '</div>' +
      '<div id="mh-gwp-promo-msg" style="font-size:12px; margin-top:6px; display:none;"></div>';

    cartFooter.insertBefore(wrapper, cartFooter.firstChild);

    if (appliedCodes.length > 0) {
      showAppliedCodes();
    }

    document.getElementById('mh-gwp-promo-btn').addEventListener('click', handlePromoCode);
    document.getElementById('mh-gwp-promo-input').addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handlePromoCode();
      }
    });
  }

  function handlePromoCode() {
    const input = document.getElementById('mh-gwp-promo-input');
    const msgEl = document.getElementById('mh-gwp-promo-msg');
    const code = input.value.trim().toUpperCase();

    if (!code) return;

    const matchingRule = rules.find(function (r) {
      return r.discountCode && r.discountCode.toUpperCase() === code;
    });

    if (matchingRule) {
      if (!appliedCodes.includes(code)) {
        appliedCodes.push(code);
        sessionStorage.setItem(APPLIED_CODES_KEY, JSON.stringify(appliedCodes));
      }

      input.value = '';
      msgEl.textContent = 'Code applied! A free gift has been added to your cart.';
      msgEl.style.color = '#1a1a1a';
      msgEl.style.display = 'block';
      showAppliedCodes();
      checkCart();
    } else {
      msgEl.textContent = 'This code is not valid for a free gift.';
      msgEl.style.color = '#d72c0d';
      msgEl.style.display = 'block';
    }
  }

  function showAppliedCodes() {
    const existing = document.getElementById('mh-gwp-applied-codes');
    if (existing) existing.remove();

    if (appliedCodes.length === 0) return;

    const wrapper = document.getElementById('mh-gwp-promo-wrapper');
    if (!wrapper) return;

    const codesDiv = document.createElement('div');
    codesDiv.id = 'mh-gwp-applied-codes';
    codesDiv.style.cssText = 'margin-top:8px;';

    codesDiv.innerHTML = appliedCodes.map(function (code) {
      return '<span style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f1f1f1;color:#1a1a1a;border-radius:4px;font-size:13px;margin-right:6px;">' +
        code +
        '<button type="button" onclick="window._mhGwpRemoveCode(\'' + code + '\')" style="background:none;border:none;color:#1a1a1a;cursor:pointer;font-size:16px;padding:0;line-height:1;">&times;</button>' +
        '</span>';
    }).join('');

    wrapper.appendChild(codesDiv);
  }

  window._mhGwpRemoveCode = function (code) {
    appliedCodes = appliedCodes.filter(function (c) { return c !== code; });
    sessionStorage.setItem(APPLIED_CODES_KEY, JSON.stringify(appliedCodes));
    showAppliedCodes();
    checkCart();
  };

  // ── Promo input visibility ─────────────────────────────────────────

  function updatePromoInputVisibility(cart) {
    var wrapper = document.getElementById('mh-gwp-promo-wrapper');
    if (!wrapper) return;

    var codeRules = rules.filter(function (r) { return r.discountCode; });
    if (codeRules.length === 0) {
      wrapper.style.display = 'none';
      return;
    }

    var anyEligible = codeRules.some(function (rule) {
      if (rule.conditionType === 'specific_product') {
        if (!rule.qualifyingProductId) return false;
        return cart.items.some(function (item) {
          if (item.properties && item.properties[GWP_CART_PROPERTY]) return false;
          if (rule.qualifyingVariantId) return item.variant_id === rule.qualifyingVariantId;
          return item.product_id === rule.qualifyingProductId;
        });
      }
      return false;
    });

    var hasAppliedCode = appliedCodes.length > 0;
    wrapper.style.display = (anyEligible || hasAppliedCode) ? '' : 'none';
  }

  // ── Eligibility checks ───────────────────────────────────────────────

  function isEligible(rule, cart) {
    if (rule.discountCode) {
      if (!appliedCodes.includes(rule.discountCode.toUpperCase())) {
        return false;
      }
    }

    switch (rule.conditionType) {
      case 'minimum_spend': {
        var subtotal = cart.total_price / 100;
        // Exclude from the qualifying subtotal:
        //  - existing GWP gift lines (so adding a gift doesn't push the
        //    cart back over the threshold and lock the gift in)
        //  - gift card products (Shopify's `gift_card` flag — auto safety net)
        //  - any product listed in rule.excludedProductIds (manual list,
        //    set per-discount in the config page for digital products,
        //    services, etc.)
        var excludedIds = rule.excludedProductIds || [];
        var excluded = cart.items.reduce(function (sum, item) {
          if (item.properties && item.properties[GWP_CART_PROPERTY]) {
            return sum + (item.final_line_price / 100);
          }
          if (item.gift_card) {
            return sum + (item.final_line_price / 100);
          }
          if (excludedIds.indexOf(item.product_id) !== -1) {
            return sum + (item.final_line_price / 100);
          }
          return sum;
        }, 0);
        return (subtotal - excluded) >= rule.spendThreshold;
      }

      case 'specific_product': {
        if (!rule.qualifyingProductId) return false;
        return cart.items.some(function (item) {
          if (item.properties && item.properties[GWP_CART_PROPERTY]) return false;
          if (rule.qualifyingVariantId) {
            return item.variant_id === rule.qualifyingVariantId;
          }
          return item.product_id === rule.qualifyingProductId;
        });
      }

      case 'none':
        return true;

      default:
        return false;
    }
  }

  function clearCodeForRule(rule) {
    if (!rule.discountCode) return;
    var code = rule.discountCode.toUpperCase();
    appliedCodes = appliedCodes.filter(function (c) { return c !== code; });
    sessionStorage.setItem(APPLIED_CODES_KEY, JSON.stringify(appliedCodes));
    showAppliedCodes();
    var wrapper = document.getElementById('mh-gwp-promo-wrapper');
    if (wrapper && appliedCodes.length === 0) {
      wrapper.style.display = 'none';
    }
  }

  function dismissGift(ruleName) {
    if (!dismissedGifts.includes(ruleName)) {
      dismissedGifts.push(ruleName);
      sessionStorage.setItem(DISMISSED_GIFTS_KEY, JSON.stringify(dismissedGifts));
    }
  }

  // ── Core logic ────────────────────────────────────────────────────────

  function findGiftLineInCart(cart, rule) {
    return cart.items.find(function (item) {
      return item.variant_id === rule.giftVariantId &&
        item.properties &&
        item.properties[GWP_CART_PROPERTY] === rule.name;
    });
  }

  // Remove any cart line whose _gwp_rule property points to a rule that
  // no longer exists in `rules`. This prevents orphaned gifts from
  // sticking around after a discount is deleted/disabled — without this,
  // the customer would be charged full price at checkout because no
  // active discount remains to zero the gift's price.
  async function removeOrphanedGifts(cart) {
    if (!rulesReady) return cart;
    var liveRuleNames = rules.map(function (r) { return r.name; });
    var orphans = cart.items.filter(function (item) {
      return item.properties && item.properties[GWP_CART_PROPERTY] &&
        !liveRuleNames.includes(item.properties[GWP_CART_PROPERTY]);
    });
    for (var i = 0; i < orphans.length; i++) {
      try {
        await removeFromCart(orphans[i].key);
      } catch (e) { /* continue */ }
    }
    if (orphans.length > 0) {
      cart = await getCart();
      refreshCartUI();
    }
    return cart;
  }

  async function checkCart() {
    if (processing) return;
    processing = true;

    try {
      var cart = await getCart();

      // 1. Scrub orphaned gifts whose rule no longer exists. This is the
      //    primary safeguard against the "stale rule" bug — even if the
      //    Liquid metafield was somehow stale, the live `rules` array is
      //    authoritative, and any gift not backed by a live rule is removed.
      cart = await removeOrphanedGifts(cart);

      // 2. Don't auto-add anything until rules are confirmed loaded.
      if (!rulesReady) {
        return;
      }

      for (var i = 0; i < rules.length; i++) {
        var rule = rules[i];

        if (rule.giftOptions && rule.giftOptions.length > 1) {
          var eligible = isEligible(rule, cart);
          if (!eligible) {
            for (var g = 0; g < rule.giftOptions.length; g++) {
              var giftOpt = rule.giftOptions[g];
              var giftLine = cart.items.find(function (item) {
                return item.variant_id === giftOpt.variantId &&
                  item.properties && item.properties[GWP_CART_PROPERTY] === rule.name;
              });
              if (giftLine) {
                await removeFromCart(giftLine.key);
                cart = await getCart();
                clearCodeForRule(rule);
                refreshCartUI();
              }
            }
          }
          continue;
        }

        var giftInCart = findGiftLineInCart(cart, rule);
        var eligible = isEligible(rule, cart);

        if (eligible && !giftInCart && !dismissedGifts.includes(rule.name)) {
          try {
            var variantRes = await fetch('/variants/' + rule.giftVariantId + '.js', { headers: { 'Accept': 'application/json' } });
            if (variantRes.ok) {
              var variantData = await variantRes.json();
              if (!variantData.available) continue;
            } else { continue; }
          } catch { continue; }
          var properties = {};
          properties[GWP_CART_PROPERTY] = rule.name;
          await addToCart(rule.giftVariantId, properties);
          cart = await getCart();
          refreshCartUI();
        } else if (!eligible && giftInCart) {
          await removeFromCart(giftInCart.key);
          cart = await getCart();
          clearCodeForRule(rule);
          refreshCartUI();
        } else if (!eligible && dismissedGifts.includes(rule.name)) {
          dismissedGifts = dismissedGifts.filter(function(n) { return n !== rule.name; });
          sessionStorage.setItem(DISMISSED_GIFTS_KEY, JSON.stringify(dismissedGifts));
        }
      }
      updateDrawerGiftMessage(cart);
      updatePromoInputVisibility(cart);

      if (cartPageNeedsReload) {
        cartPageNeedsReload = false;
        window.location.reload();
        return;
      }
    } catch (e) {
      console.error('[MH GWP] Error checking cart:', e);
    } finally {
      processing = false;
    }
  }

  // ── Cart drawer gift message ────────────────────────────────────────

  function updateDrawerGiftMessage(cart) {
    document.querySelectorAll('.mh-gwp-drawer-msg').forEach(function (el) { el.remove(); });
    if (window.location.pathname === '/cart') return;

    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (!rule.giftOptions || rule.giftOptions.length <= 1) continue;

      var eligible = isEligible(rule, cart);
      if (!eligible) continue;

      var giftAlreadyInCart = rule.giftOptions.some(function (opt) {
        return cart.items.some(function (item) {
          return item.variant_id === opt.variantId &&
            item.properties && item.properties[GWP_CART_PROPERTY] === rule.name;
        });
      });

      if (giftAlreadyInCart) continue;

      var drawerItems = document.querySelectorAll('[data-cart-drawer] [data-cart-item]');
      drawerItems.forEach(function (itemEl) {
        var variantEl = itemEl.querySelector('[data-remove-button]');
        if (!variantEl) return;
        var variantId = parseInt(variantEl.getAttribute('data-variantid'));

        var isQualifying = false;
        if (rule.qualifyingVariantId) {
          isQualifying = variantId === rule.qualifyingVariantId;
        } else if (rule.qualifyingProductId) {
          isQualifying = cart.items.some(function (item) {
            return item.variant_id === variantId && item.product_id === rule.qualifyingProductId;
          });
        }

        if (!isQualifying) return;

        var priceEl = itemEl.querySelector('.font-bold');
        if (priceEl && !priceEl.parentNode.querySelector('.mh-gwp-drawer-msg')) {
          var msg = document.createElement('p');
          msg.className = 'mh-gwp-drawer-msg text-sm font-semibold mt-0.5';
          msg.textContent = 'Add your free gift at checkout';
          priceEl.parentNode.insertBefore(msg, priceEl.nextSibling);
        }
      });
    }
  }

  // ── UI refresh ────────────────────────────────────────────────────────

  var cartPageNeedsReload = false;

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:quantity-updated'));
    if (window.location.pathname === '/cart') {
      cartPageNeedsReload = true;
    }
  }

  // ── Observe cart changes ──────────────────────────────────────────────

  var checkCartTimer = null;
  function debouncedCheckCart() {
    clearTimeout(checkCartTimer);
    checkCartTimer = setTimeout(checkCart, 500);
  }

  function observeCartChanges() {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var promise = originalFetch.apply(this, arguments);

      if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
        var body = arguments[1]?.body;
        var isOurCall = false;
        if (typeof body === 'string') {
          try { isOurCall = JSON.parse(body)?.items?.[0]?.properties?.[GWP_CART_PROPERTY]; } catch {}
        }
        if (!isOurCall) {
          promise.then(debouncedCheckCart);
        }
      }

      return promise;
    };

    document.addEventListener('shopify:section:load', debouncedCheckCart);

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'data-state') {
          var el = m.target;
          if (el.getAttribute('data-state') === 'open' && el.getAttribute('data-drawer') === 'cart') {
            setTimeout(function () {
              injectPromoCodeInput();
              getCart().then(function (cart) {
                updateDrawerGiftMessage(cart);
                updatePromoInputVisibility(cart);
              });
            }, 100);
          }
        }
      });
    });
    var drawerEl = document.querySelector('[data-drawer="cart"]');
    if (drawerEl) {
      observer.observe(drawerEl, { attributes: true, attributeFilter: ['data-state'] });
    }

    document.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-remove-button]') || e.target.closest('a.cart-edit-remove-btn');
      if (!removeBtn) return;

      var cartItemEl = removeBtn.closest('[data-cart-item]');
      if (!cartItemEl) return;

      var variantId = null;
      if (removeBtn.dataset && removeBtn.dataset.variantid) {
        variantId = parseInt(removeBtn.dataset.variantid);
      }

      getCart().then(function (cart) {
        cart.items.forEach(function (item) {
          if (item.properties && item.properties[GWP_CART_PROPERTY]) {
            var ruleName = item.properties[GWP_CART_PROPERTY];
            if (variantId && item.variant_id === variantId) {
              dismissGift(ruleName);
            }
            if (!variantId && cartItemEl.dataset.lineIndex) {
              var lineIndex = parseInt(cartItemEl.dataset.lineIndex);
              if (cart.items[lineIndex - 1] && cart.items[lineIndex - 1].properties &&
                  cart.items[lineIndex - 1].properties[GWP_CART_PROPERTY]) {
                dismissGift(cart.items[lineIndex - 1].properties[GWP_CART_PROPERTY]);
              }
            }
          }
        });
      });
    }, true);

    if (window.location.pathname === '/cart') {
      setInterval(debouncedCheckCart, 2000);
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
